/**
 * Service-agreement clauses (single source of truth).
 *
 * Imported by:
 *   - src/config/brand.js (React-side brand config used by QuotePage
 *     and AgreementView).
 *   - api/quote/submit.js (server-side submit handler that persists a
 *     snapshot of the exact clauses the customer signed at sign time).
 *
 * Lives under src/ rather than api/ because Create React App refuses
 * imports outside src/. Vercel's serverless function bundler walks
 * the import graph fine in either direction.
 *
 * Bumping any of these clauses or consent text MUST be paired with a
 * bump to AGREEMENT_VERSION in api/quote/submit.js so signed-at
 * snapshots remain unambiguous over time.
 */

import { normalizeSeasonalPricing, seasonalSummaryLines } from './seasonal-pricing.js';

// Card-processing fee passed through on ALL credit card payments
// (monthly autopay and one-time card-at-service charges). ACH carries
// no fee. Shared by the quote page's fee-disclosure UI and
// api/quote/submit.js (recurring-template fee line). The 3.5% figure
// is also written out in the clause prose below; keep them in sync.
export const CC_FEE_RATE = 0.035;

export const normalizeVisitsPerWeek = (value) => value === 0.5 ? 0.5 : value === 2 ? 2 : 1;

// Seasonal quotes keep the weekly clauses and add one card stating both
// seasons, so the agreement text does not change with the calendar and the
// twice-weekly visit and credit terms still cover the twice-weekly season.
const seasonalCard = (seasonal) => {
  const [summer, winter] = seasonalSummaryLines(seasonal);
  const twice = ['summer', 'winter'].filter((name) => seasonal[name].visitsPerWeek === 2);
  const twiceTerms = twice.length
    ? ` In the ${twice.join(' and ')} season your monthly rate covers eight visits over four weeks, two each week; each additional scheduled visit is billed at 12.5% of the monthly rate, and each missed visit is credited at 12.5%.`
    : '';
  return {
    title: 'Seasonal service and rates',
    body: `${seasonal.startWindow ? `Service begins ${seasonal.startWindow}; we will confirm the first visit date with you. ` : ''}Your service changes with the season. ${summer}. ${winter}.${twiceTerms} We adjust your monthly charge and visit schedule at the start of each season.`,
  };
};

// Adapt only the cadence clauses, preserving the selected payment terms.
// Prices remain a separate quote field; the weekly amount covers both visits.
export const getMonthlyAgreementCards = (cards, visitsPerWeek, seasonalPricing) => {
  if (!Array.isArray(cards)) return [];
  const seasonal = normalizeSeasonalPricing(seasonalPricing);
  if (seasonal) {
    const at = cards.findIndex((card) => card.title === 'Billed monthly on the 1st');
    const insertAt = at === -1 ? cards.length : at + 1;
    const alternateWeeks = seasonal.summer.visitsPerWeek === 0.5 || seasonal.winter.visitsPerWeek === 0.5;
    const selected = alternateWeeks ? cards.map(card => {
      if (card.title === 'Billed monthly on the 1st') return { ...card, body: 'The seasonal monthly amounts shown in this agreement cover the stated number of visits. Additional scheduled visits are billed at that season\'s per-visit rate; missed visits on our part are credited at the same rate. Your first month is prorated for the visits provided.' };
      return { ...card, body: card.body.replace('your weekly service', 'your seasonal service').replace('missed week', 'missed visit'),
        ...(card.details ? { details: card.details.replace('the same day each week', 'your scheduled service day') } : {}) };
    }) : cards;
    return [...selected.slice(0, insertAt), seasonalCard(seasonal), ...selected.slice(insertAt)];
  }
  if (normalizeVisitsPerWeek(visitsPerWeek) !== 2) return cards;

  return cards.map((card) => {
    if (card.title === 'Your first visit') {
      return { ...card, body: card.body.replace('your weekly service', 'your twice-weekly service') };
    }
    if (card.title === 'Billed monthly on the 1st') {
      return {
        ...card,
        body: card.body.replace(
          /^Our standard rate covers four weekly visits per month\..*?25% per missed visit\./,
          'Your monthly rate covers eight visits over four weeks, with two visits each week. Each additional scheduled visit is billed at 12.5% of the monthly rate. If we provide fewer than eight visits in a month because of weather or a missed visit on our part, we credit 12.5% per missed visit.'
        ),
      };
    }
    if (card.title === 'Pool access is your responsibility') {
      return {
        ...card,
        body: card.body.replace('your service day', 'each service day').replace('missed week', 'missed visit'),
        details: card.details.replace('the same day each week', 'the same days each week'),
      };
    }
    return card;
  });
};

const MONTHLY_CARDS = [
    {
      title: 'Your first visit',
      body: "It is important that your pool be clean, algae free, and chemically balanced when we begin your weekly service. Some pools may require additional work before this can begin. On your first visit we will do a full inspection and communicate any additional work that needs to be done before we can begin.",
    },
    {
      title: 'Billed monthly on the 1st',
      body: "Our standard rate covers four weekly visits per month. Occasionally the calendar gives us a month with five weeks; when that happens we simply add the cost of that one extra visit (25%) to keep your pool sparkling. Conversely, if we visit fewer than four times in a month (bad weather, something on us, etc.), we automatically discount your bill 25% per missed visit. If you pay by credit card, a 3.5% card processing fee is added to each bill; pay by bank account (ACH) and there's no fee at all. No hidden fees, just fair service.",
    },
    {
      title: 'Prorated first month',
      body: "Your first invoice is prorated based on the number of times we provide service that month.",
    },
    {
      title: 'Photo report after every visit',
      body: "Your technician sends a timestamped photo report by email after each service so you can see the finished pool, even if you're not home.",
    },
    {
      title: 'Pool access is your responsibility',
      body: "We need access on your service day to keep your pool on schedule. If we can't get in, the visit is skipped and the missed week is not credited.",
      details: "If your property requires a key, please leave us with two copies. If we arrive and can't access the pool (locked gate, changed locks, dog out, anything else), your tech will skip the visit and return on your next regular service day. If you'd rather we come back the same week, there's a $45 trip charge. Our techs won't jump or climb walls or fences. We'll always do our best to service on the same day each week, and if your regular tech can't make it, another tech will cover. Help us help you by making sure we can reach the pool.",
    },
    {
      title: 'Filtration is your responsibility',
      body: 'Your pump needs to run long enough to keep the water moving. Aim for at least 8 hours a day in warm months and 5 hours a day in cool months.',
      details: "Insufficient filtration causes algae and cloudy water and means we have to use more chemicals to fix it; those issues are not covered by our service guarantee. Topping off pool water is also on you: when the level drops below the skimmer, the pump can't pull water through the filter. Our techs don't add water to any pool, spa, or water feature, but if your pool needs a fill we'll leave a door hanger so you know. (If you'd rather skip the manual fills, ask us about installing an automatic water leveler.)",
    },
    {
      title: 'Weather conditions',
      body: "On rainy days we focus on chemistry and baskets. After windy weather, full debris cleanup may take more than one visit.",
      details: "On rainy days your tech will check chemistry, add chemicals, and empty the skimmer and pump baskets. Full cleanings while it's pouring don't make sense. After a windy stretch, debris keeps blowing in for a few days; sometimes it takes more than one visit to fully clear out a pool because techs are dealing with the same conditions across their whole route that day.",
      // Skipped on chem-only and chem-and-brush plans because the
      // clause is about full-service debris cleanup, which isn't part
      // of those tiers' scope.
      excludeForTiers: ['chem_only', 'chem_brush'],
    },
    {
      title: 'Pool covers',
      body: "Please remove safety nets and solar covers before your service day. We don't put them back on after the visit. Automatic covers should be open on your service day.",
      details: "Safety nets and solar pool covers need to be removed prior to service. We don't put safety nets or solar covers back on after servicing the pool. Automatic pool covers must be open on your service day so we can do a full visit.",
    },
    {
      title: 'Equipment repairs are not included',
      body: "Mechanical problems with pumps, filters, heaters, salt cells, automation, plaster, or tile aren't included in your monthly rate. If something goes wrong, we'll let you know what the repair would be. Cabana can handle all repairs and upgrades you need for pools and spas.",
    },
    {
      title: 'Specialty treatments billed separately',
      body: 'Standard chemicals (chlorine, acid, balancing) are included. The treatments below are recommended as needed and billed at the time of service.',
      addons: [
        { name: 'DE / Cartridge Filter Cleaning', frequency: 'Every 3 months or as needed' },
        { name: 'Algaecides', frequency: 'Once a year' },
        { name: 'Conditioner (Stabilizer)', frequency: '1-2 times a year' },
        { name: 'Diatomaceous Earth', frequency: 'As needed' },
        { name: 'Salt Cell Cleaning', frequency: 'Every 3-6 months', requires: 'salt' },
        { name: 'Spa Drain, Clean & Fill', frequency: 'Every 3-4 months', requires: 'spa-only' },
      ],
    },
    {
      title: 'Cancel anytime, 30 days notice',
      body: "We're excited to take care of your pool with this month-to-month service. It keeps going until you or we decide it's time to stop. If you ever need to cancel, just let us know in writing 30 days in advance. When service ends, any final payments are due.",
    },
    {
      title: 'Auto-pay authorization',
      body: "You authorize Cabana to charge the credit card or bank account you provided above on the 1st of each month for that month's service, plus any approved one-time charges or specialty treatments. Card payments include a 3.5% card processing fee; bank account (ACH) payments have no fee. We email a receipt for every charge. You can update or switch the payment method anytime by replying to any invoice (switching to ACH drops the card fee).",
    },
];

// Pay-by-check variant of the monthly agreement, selected per-deal by the
// `pay-by-check` tag on the Sell deal (see api/quote/[dealId].js). It exists
// for accounts we bill by invoice rather than autopay, where asking for a
// card or bank account at signing is wrong: the customer never enters
// payment details and nothing is ever charged automatically.
//
// Derived from MONTHLY_CARDS by title rather than copied, so a later edit to
// any shared clause (access, filtration, covers, cancellation, ...) lands in
// both agreements and the two sets cannot drift apart. Only the two clauses
// that actually talk about charging a payment method are overridden:
// the billing clause drops the card-processing-fee sentence (there is no
// card), and the auto-pay clause is replaced outright.
const MONTHLY_CHECK_CARD_OVERRIDES = {
  'Billed monthly on the 1st': {
    body: "Our standard rate covers four weekly visits per month. Occasionally the calendar gives us a month with five weeks; when that happens we simply add the cost of that one extra visit (25%) to keep your pool sparkling. Conversely, if we visit fewer than four times in a month (bad weather, something on us, etc.), we automatically discount your bill 25% per missed visit. No hidden fees, just fair service.",
  },
  'Auto-pay authorization': {
    title: 'Invoiced monthly, paid by check',
    body: "No credit card or bank account is required for this account and none is kept on file. We email you an invoice on the 1st of each month for that month's service, plus any approved one-time charges or specialty treatments, and you pay by check. Nothing is ever charged automatically. If you'd rather switch to automatic payment later, reply to any invoice and we'll set it up.",
  },
};

const MONTHLY_CHECK_CARDS = MONTHLY_CARDS.map((card) => (
  MONTHLY_CHECK_CARD_OVERRIDES[card.title]
    ? { ...card, ...MONTHLY_CHECK_CARD_OVERRIDES[card.title] }
    : card
));

// Commercial consent, personalized to the property and the quoted terms at
// render time and frozen with those values in the signed snapshot. Called with
// no arguments it yields the generic form used for the template hash.
export const buildCommercialConsentText = ({ propertyName, terms, prepared } = {}) => {
  const who = String(propertyName || '').trim() || 'the property or organization named above';
  if (prepared) return `I represent that I am authorized to enter into this agreement on behalf of ${who}. I agree to the service, itemized pricing, discounts, and additional agreed terms shown above. The payment terms are ${prepared.paymentTerms}, payable by check or ACH. I can cancel anytime with 30 days written notice.`;
  const termsLabel = terms === 'net60' ? 'Net 60' : 'Net 30';
  return `I represent that I am authorized to enter into this agreement on behalf of ${who}. I agree to the monthly pool service, pricing, and billing terms shown above. Cabana will invoice monthly, ${termsLabel}, payable by check or ACH. I can cancel anytime with 30 days written notice.`;
};

export const SERVICE_AGREEMENT = {
  intro: "Here's what you're agreeing to. Plain English, no fine print surprises.",
  cards: MONTHLY_CARDS,
  monthlyCheckCards: MONTHLY_CHECK_CARDS,
  consentText: 'I authorize Cabana to charge the payment method above on the 1st of each month for monthly pool service, plus approved one-time and specialty charges as described. I have read the agreement above and agree to the terms. I can cancel anytime with 30 days notice.',
  // Pay-by-check consent. No charge authorization, because nothing is ever
  // charged. The esign disclosure is unchanged from the standard monthly one
  // ("By clicking Start My Service..."), which is already accurate here, so
  // there is deliberately no monthlyCheckEsignDisclosure to keep in sync.
  monthlyCheckConsentText: 'I agree to monthly pool service at the price above, invoiced monthly and paid by check, plus approved one-time and specialty charges as described. No credit card or bank account is required or kept on file, and nothing will be charged automatically. I have read the agreement above and agree to the terms. I can cancel anytime with 30 days notice.',
  esignDisclosure: "By clicking Start My Service you're signing this agreement electronically under the U.S. ESIGN Act. We'll email you a copy. You can request a paper copy or withdraw consent by replying to any invoice.",
  oneTimeEsignDisclosure: "By clicking Confirm Services you're signing this agreement electronically under the U.S. ESIGN Act. We'll email you a copy. You can request a paper copy or withdraw consent by replying to any invoice.",
  oneTimeCards: [
    {
      title: 'Yard access is your responsibility',
      body: "We need access to the work area on your scheduled service day. If our tech can't get in, a $45 trip charge applies for the return visit.",
    },
    {
      title: 'Warranty',
      body: "All parts and materials carry the manufacturer's standard warranty. Labor is warranted for 30 days from completion.",
    },
    {
      title: 'Liability',
      body: "Cabana carries multimillion-dollar liability insurance. You're responsible for any pre-existing conditions or damage not caused by our work.",
    },
    {
      title: 'Additional work',
      body: "Any work beyond the original scope requires your written approval and may incur additional charges. We won't do extra work without your sign-off.",
    },
    {
      title: 'Payment',
      body: "Past due accounts are subject to a 1.5% monthly service charge. If your account goes to collections, you're responsible for collection costs.",
    },
    {
      title: 'Charge authorization',
      body: 'You authorize Cabana to charge the credit card or bank account you provided above for the one-time services listed in this proposal at the time of service. Card payments include a 3.5% card processing fee; bank account (ACH) payments have no fee. We email a receipt for every charge.',
    },
  ],
  oneTimeConsentText: 'I authorize Cabana to charge the payment method above for the one-time services listed in this proposal at the time of service. I have read the agreement above and agree to the terms.',
  // Net 30 variant: same one-time scope clauses (yard access, warranty,
  // liability, additional work) but the payment block is replaced --
  // no card on file, invoice issued at completion, payment due 30 days
  // from invoice date. Selected per-deal by the `net-30` tag on the
  // Sell deal (see api/quote/[dealId].js backfill).
  oneTimeNet30Cards: [
    {
      title: 'Yard access is your responsibility',
      body: "We need access to the work area on your scheduled service day. If our tech can't get in, a $45 trip charge applies for the return visit.",
    },
    {
      title: 'Warranty',
      body: "All parts and materials carry the manufacturer's standard warranty. Labor is warranted for 30 days from completion.",
    },
    {
      title: 'Liability',
      body: "Cabana carries multimillion-dollar liability insurance. You're responsible for any pre-existing conditions or damage not caused by our work.",
    },
    {
      title: 'Additional work',
      body: "Any work beyond the original scope requires your written approval and may incur additional charges. We won't do extra work without your sign-off.",
    },
    {
      title: 'Net 30 invoicing',
      body: "We invoice for the services in this proposal upon completion. Payment is due within 30 days of the invoice date.",
    },
    {
      title: 'Past due accounts',
      body: "Invoices unpaid after 30 days are subject to a 1.5% monthly service charge. If your account goes to collections, you're responsible for collection costs.",
    },
  ],
  oneTimeNet30ConsentText: 'I agree to the services and pricing in this proposal. Cabana will invoice me at completion and I will remit payment within 30 days of the invoice date.',
  oneTimeNet30EsignDisclosure: "By clicking Confirm Services you're signing this agreement electronically under the U.S. ESIGN Act. We'll email you a copy. You can request a paper copy or withdraw consent by replying to any invoice.",

  // Commercial monthly service agreement. Distinct from residential: the
  // signer is an authorized representative of a property or organization
  // (HOA, property manager, school), billing is invoiced monthly on
  // terms (paid by check or ACH), and there is no card-on-file / auto-pay
  // authorization. Seasonal pricing (summer/winter) is rendered from the
  // quote when present. Bumping any clause here MUST bump AGREEMENT_VERSION
  // in api/quote/submit.js so signed snapshots stay unambiguous.
  commercialCards: [
    {
      title: 'Your first visit',
      body: "It is important that the pool be clean, algae free, and chemically balanced when we begin service. Some pools may require additional work before regular service can begin. On the first visit we complete a full inspection and communicate any additional work needed before we start.",
    },
    {
      title: 'Monthly service at the agreed rate',
      body: "Service is provided at the monthly rate and visit frequency shown above. Where summer and winter rates are listed, the seasonal rate applies for those months. The rate covers the scheduled weekly visits for the month.",
    },
    {
      title: 'Invoiced monthly, Net 30',
      body: "We invoice monthly for that month's service. Payment is due within 30 days of the invoice date, by check or ACH. Invoices are sent to the billing contact shown above. No card is kept on file.",
    },
    {
      title: 'Prorated first month',
      body: "The first invoice is prorated based on the number of service visits provided in that first partial month, then full monthly invoicing follows.",
    },
    {
      title: 'Filter cleaning billed separately',
      body: "Standard chemicals (chlorine, acid, balancing) are included in the monthly rate. Filter cleaning is billed separately at the rate shown above, performed every three months or as needed.",
    },
    {
      title: 'Property access is your responsibility',
      body: "We need access to the pool on the scheduled service day to keep service on schedule. Please provide a gate code or key and keep the on-site contact reachable. If we can't get in, the visit is skipped and the missed week is not credited.",
      details: "If the property requires a key, please leave us with two copies. If we arrive and can't access the pool (locked gate, changed locks, dog out, anything else), the tech will skip the visit and return on the next regular service day. Our techs won't jump or climb walls or fences.",
    },
    {
      title: 'Photo report after every visit',
      body: "The technician sends a timestamped photo report by email after each service so the property team can see the finished pool.",
    },
    {
      title: 'Equipment repairs are not included',
      body: "Mechanical problems with pumps, filters, heaters, salt cells, automation, plaster, or tile aren't included in the monthly rate. If something needs repair we'll quote it separately. Cabana can handle all repairs and upgrades for pools and spas.",
    },
    {
      title: 'Specialty treatments billed separately',
      body: 'Standard chemicals are included. Specialty treatments (filter cleaning, algaecides, conditioner, salt-cell cleaning, and similar) are recommended as needed and billed at the time of service.',
    },
    {
      title: 'Cancel anytime, 30 days notice',
      body: "This is a month-to-month service agreement. It continues until either party ends it. To cancel, provide written notice 30 days in advance. When service ends, any final invoices are due on their terms.",
    },
    {
      title: 'Authority to sign',
      body: "By signing, you represent that you are authorized to enter into this agreement on behalf of the property or organization named above and to designate the billing contact shown.",
    },
  ],
  commercialConsentText: buildCommercialConsentText(),
  commercialEsignDisclosure: "By clicking Sign Agreement you're signing this agreement electronically under the U.S. ESIGN Act. A link to your signed copy appears right after you sign, where you can print it or save it as a PDF. You can request a paper copy or withdraw consent by replying to any invoice or email from us.",

  // Structured commercial proposal content, mirroring the PDFMonkey
  // "Commercial Quote v2" template so the web signing page reads as a real
  // multi-section proposal (cover, about, pricing, what's included,
  // partnership responsibilities, additional services, terms), not a wall of
  // clause cards. Copy is ported from that template. The complete content,
  // consent, and electronic-signature copy determine the template hash.
  commercial: {
    proposalTitle: 'Pool Service Proposal',
    // About copy is the PDFMonkey "Commercial Quote v2" About page, amended
    // only where the web document differs from the printed one (region wording,
    // "pages" to "sections").
    about: {
      intro: [
        'Thanks for considering Cabana for your commercial pool service needs. We are commercial pool specialists serving over 300 properties across California. From HOAs and apartment communities to resorts and commercial facilities, we keep high-use, high-visibility pools crystal clear all year long, compliant, and ready for swimmers.',
        'Cabana was built for the demands of commercial pool care. Our technicians are CPO-certified by the Pool & Hot Tub Alliance and carry SPPA insurance, the gold standards in the industry. We stay ahead of health code requirements, handle complex commercial equipment systems, and deliver consistent, proactive service that gives property managers peace of mind.',
        'Our support team operates seven days a week and responds quickly. Whether you need a quick answer or an emergency visit, we have you covered.',
      ],
      whyTitle: 'Why properties choose Cabana',
      // Each item renders as a titled card. Older signed snapshots carry plain
      // strings here; the renderer accepts both shapes.
      why: [
        { title: 'Over 300 commercial customers', body: 'HOAs, property managers, and commercial developers trust Cabana to manage their pools.' },
        { title: 'Industry-leading technicians', body: 'Every tech is CPO-certified and trained specifically for commercial systems and standards.' },
        { title: 'Detailed digital reporting', body: 'After every visit, you receive a full digital service report with checklists, chemical readings, technician notes, and photos included.' },
        { title: 'Daily support', body: 'Help from real humans, 8am to 5pm every day. Responsive support that does not disappear when you need it.' },
        { title: 'Compliance experts', body: 'We operate in full alignment with local health codes and county guidelines. No guesswork. No gray areas.' },
      ],
      closing: 'At Cabana, we believe in showing our work and earning your trust with every visit, every report, and every conversation. The sections below outline your customized service proposal. If you have any questions, we are here and happy to help. We look forward to becoming your trusted partner in pool care.',
    },
    whatsIncluded: [
      'Check and balance pool chemicals for optimum water quality',
      'Manually vacuum pool surfaces as needed',
      'Clean all baskets (skimmer and pump)',
      'Brush walls and steps as needed',
      'Skim surface debris',
      'Inspect system and backwash filter as needed',
      'Treat for algae',
      'Detailed post-service report after each visit with photos, chemical readings, and technician notes',
    ],
    responsibilities: {
      yours: [
        'Maintain landscaping around the pool area and keep vegetation trimmed back from equipment and decking.',
        'Pay invoices on the terms shown. Monthly invoicing begins with service.',
        'Ensure safe technician access to the pool and equipment areas.',
        'Remove toys, floats, covers, and obstructions before service.',
        'Notify Cabana immediately of equipment issues or safety concerns.',
      ],
      ours: [
        'Arrive on all scheduled service days (weather and holidays excepted).',
        'Maintain water chemistry within health department compliance ranges.',
        'Perform thorough visual equipment inspections at every visit.',
        'Deliver detailed digital reports within 24 hours of service.',
        'Respond to service requests and inquiries within 24 hours.',
        'Maintain all CPO certifications and SPPA insurance coverage.',
        'Communicate proactively about issues, recommendations, and upcoming maintenance.',
        'Ensure full compliance with local and state health codes.',
      ],
    },
    additionalServices: [
      {
        title: 'Filter and salt system cleaning',
        body: 'Clean filters ensure your pool stays clean and clear all year. Our technicians perform complete filter breakdowns, cleaning, and reassembly four times annually, billed after completion at the filter cleaning rate shown above. For pools with salt chlorine generators, we clean the cell four times per year under the same terms. This ensures optimal performance and extended equipment life.',
      },
      {
        title: 'Annual algae prevention treatment',
        body: 'Conquer the green with our expert algae treatments. Our once-yearly algae prevention treatment creates a protective barrier against algae, bacteria, and organic growth. We automatically apply this treatment at $100 annually, billed only after completion.',
      },
      {
        title: 'Pool and spa draining',
        body: 'Commercial pools occasionally require full drains when total dissolved solids or stabilizer levels exceed safe operating ranges. We monitor your water chemistry year-round and recommend draining when necessary, typically once per year for pools and every three months for high-use spas. We handle the drain and refill in compliance with local water discharge regulations.',
      },
    ],
    terms: [
      {
        title: 'Weather and holiday adjustments',
        body: "Safety first. During rain we may perform modified service (water balancing and basket emptying only). In hazardous conditions such as lightning or flooding we skip service and resume on the next scheduled day. Observed holidays are New Year's Day, Thanksgiving, and Christmas week. No service credits are issued for weather or holiday skips, as the annual schedule includes two months with five visits at no additional charge.",
      },
      {
        title: 'Scope and limitations',
        intro: 'Cabana maintains water chemistry and performs routine service. We are not responsible for:',
        bullets: [
          'Pool surface issues: plaster deterioration, PebbleTec staining, etching, or calcium deposits',
          'Tile damage from any source',
          'Equipment failures, leaks, or mechanical breakdowns',
          'Weather damage, vandalism, or force majeure events',
          'Impacts from landscape maintenance or other contractors',
        ],
        body: "Special services such as acid washing, power washing, or emergency draining require written approval and are performed at the client's risk. We aim for optimal water chemistry but are not liable for bather discomfort or reactions.",
      },
      {
        title: 'Contract terms',
        body: 'This is a month-to-month service agreement. Either party may terminate with 30 days written notice. Pricing and terms may be adjusted with 30 days advance notice.',
      },
    ],
  },
};
